import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Stack, styled, Slide, Collapse, alpha} from '@mui/material';
import {
  Body,
  designSystemToken,
  Headline,
  Icon,
} from '@lightricks/react-design-system';
import AssetCard from '@contentlab/components/asset-card';
import {ViewMode} from '@contentlab/components/asset-card/AssetCard';
import useScrollPosition from '@contentlab/hooks/use-scroll-position';
import {scrollbarMixins} from '@contentlab/styles/mixins';
import {FlowStatus} from '@contentlab/types/project';
import {AssetProps} from '@contentlab/types/projectProps';
import {MetadataDisplayKey} from '@contentlab/utils/featureHelpers';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import usePushNotifications from '../../hooks/use-push-notifications/usePushNotifications';

export interface AssetsListProps {
  testID?: string;
  listId: string;
  assets: AssetProps[];
  selectedAssetId?: string | null;
  playingAssetId?: string | null;
  checkedAssetIds: string[];
  onCheckAsset: (flowAsset: AssetProps, checked: boolean) => void;
  onAssetClick?: (flowAsset: AssetProps) => void;
  metadataDisplayParam?: MetadataDisplayKey;
  renameAsset?: ({
    assetId,
    name,
  }: {
    assetId: string;
    name: string;
  }) => Promise<void>;
  deleteAsset?: (asset: AssetProps) => Promise<void>;
  onActionsClick?: (assetId: string) => void;
  showCheckbox?: boolean;
  displayThumbnailIcon?: boolean;
  isCheckPermission?: boolean;
  downloadAsset?: (asset: AssetProps) => Promise<void>;
}

const Container = styled(Stack)(() => ({
  boxSizing: 'border-box',
  width: '100%',
  height: '100%',
  paddingBottom: '24px',
  direction: 'rtl',
  overflow: 'auto',
  ...scrollbarMixins.overrideScrollbarHover(),
}));

const FlipContainer = styled(Stack)(() => ({
  boxSizing: 'border-box',
  width: '100%',
  flexDirection: 'column',
  gap: '12px',
  direction: 'ltr',
}));

const FLOW_STATUS_TO_VIEW_MODE: Record<FlowStatus, ViewMode> = {
  [FlowStatus.READY]: ViewMode.Ready,
  [FlowStatus.FAILED]: ViewMode.Failed,
  [FlowStatus.PENDING]: ViewMode.InProcess,
  [FlowStatus.IN_PROGRESS]: ViewMode.InProcess,
  [FlowStatus.PENDING_USER_ACTION]: ViewMode.InProcess,
};

function AssetsList({
  testID = 'assets-list',
  listId,
  assets,
  selectedAssetId,
  playingAssetId,
  checkedAssetIds,
  onCheckAsset,
  onAssetClick,
  metadataDisplayParam,
  renameAsset,
  deleteAsset,
  onActionsClick,
  isCheckPermission,
  showCheckbox = false,
  displayThumbnailIcon = true,
  downloadAsset,
}: AssetsListProps) {
  const scrollRef = useScrollPosition<HTMLDivElement>(listId);

  const getAssetViewMode = useCallback((asset: AssetProps) => {
    if (asset.type === 'inputAsset') {
      return ViewMode.Done;
    }
    if (asset.status) {
      return FLOW_STATUS_TO_VIEW_MODE[asset.status];
    }

    return ViewMode.Done;
  }, []);

  const filteredAssets = useMemo(
    () => assets.filter((asset: AssetProps) => !asset.isArchived),
    [assets]
  );
  const [isPermissionPromptIn, setIsPermissionPromptIn] = useState(false);
  const {requestPermission} = usePushNotifications();
  useEffect(() => {
    const checkPermission = async () => {
      if (
        typeof window !== 'undefined' &&
        typeof window.Notification !== 'undefined' &&
        window.Notification.permission === 'default' &&
        isCheckPermission
      ) {
        requestPermission(CONTENT_LAB_VIEW_NAMES.PROJECT);
        setTimeout(() => {
          setIsPermissionPromptIn(true);
        }, 800);
        if (navigator.permissions) {
          try {
            const permission = await navigator.permissions.query({
              name: 'notifications',
            });
            permission.onchange = () => {
              setIsPermissionPromptIn(false);
            };
          } catch (error) {
            // Permissions API may not be supported in all browsers
          }
        }
      }
    };
    checkPermission();
  }, [isCheckPermission]);
  return (
    <Container data-testid={testID} ref={scrollRef}>
      <FlipContainer>
        <Collapse in={isPermissionPromptIn} timeout={800}>
          <Slide
            direction="down"
            timeout={800}
            in={isPermissionPromptIn}
            mountOnEnter
            unmountOnExit
            container={scrollRef.current}
          >
            <Stack
              direction="row"
              gap="12px"
              sx={{
                backgroundColor: alpha(
                  designSystemToken('semantic.bg.brand-secondary'),
                  0.5
                ),
                padding: '14px 16px',
                borderRadius: '12px',
              }}
            >
              <Icon
                name="Navigation-Notification"
                appearance="brand"
                size="medium"
              />
              <Stack gap="4px">
                <Headline size="xs">
                  {translate('Allow Notifications to Stay Updated')}
                </Headline>
                <Body size="md">
                  {translate(
                    'Click allow in the pop up above to get a heads up when your projects are ready'
                  )}
                </Body>
              </Stack>
            </Stack>
          </Slide>
        </Collapse>
        {filteredAssets.map((asset) => (
          <AssetCard
            key={asset.compositeId}
            asset={asset}
            selected={asset.compositeId === selectedAssetId}
            isPlaying={asset.compositeId === playingAssetId}
            checked={checkedAssetIds.includes(asset.id)}
            onCheck={(checked) => onCheckAsset(asset, checked)}
            onClick={onAssetClick}
            showCheckbox={showCheckbox}
            metadataDisplayParam={metadataDisplayParam}
            viewMode={getAssetViewMode(asset)}
            renameAsset={renameAsset}
            deleteAsset={async (assetToDelete: AssetProps) =>
              deleteAsset?.(assetToDelete)
            }
            onActionsClick={onActionsClick}
            displayThumbnailIcon={displayThumbnailIcon}
            downloadAsset={downloadAsset}
          />
        ))}
      </FlipContainer>
    </Container>
  );
}

export default AssetsList;
